import React, { useEffect, useState } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBCard,
  MDBCardBody,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
} from 'mdbreact';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import Loading from '../../../components/Loading';
import NavBar from '../../../components/NavBar';

// GESTAO DE FROTA > Contratos (#131)
// Cadastro dos contratos que alimentam o campo "Empresa contratada" do
// /add-frota. A empresa e cadastrada pelo modal secundario, sem sair daqui.
// Nao ha exclusao: contrato com veiculo vinculado nao pode ser apagado.
const FORM_VAZIO = {
  numero_contrato: '',
  sei: '',
  vigencia_inicio: '',
  vigencia_fim: '',
  valor: '',
  aditivo: false,
  fk_empresas_id: '',
};

// TODAS as colunas de contratos e empresas sao NOT NULL e NENHUMA tem default
// no banco — conferido em information_schema. Por isso todo campo da tela leva
// *, com uma excecao: "Aditivo" e checkbox, entao sempre envia true ou false e
// nunca chega vazio ao banco; marcar com * so faria ruido.
//
// O asterisco vai em <span> proprio para sair vermelho: passar "Campo *" como
// string no prop `label` do MDBInput deixaria o * na cor cinza do label.
const Obrigatorio = ({ texto }) => (
  <>
    {texto} <span className="red-text">*</span>
  </>
);

const LegendaObrigatorios = () => (
  <p className="grey-text mt-3 mb-0" style={{ fontSize: 12 }}>
    <span className="red-text">*</span> campos obrigatórios
  </p>
);

const soData = valor => (valor ? String(valor).substring(0, 10) : '');

const paraTela = data => {
  if (!data) return '-';
  const [ano, mes, dia] = String(data).substring(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
};

export default function ContratosFrota() {
  const [loading, setLoading] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [empresas, setEmpresas] = useState([]);

  // modal criar/editar contrato
  const [modalContrato, setModalContrato] = useState(false);
  const [editandoId, setEditandoId] = useState(null); // null = criando
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);

  // modal nova empresa
  const [modalEmpresa, setModalEmpresa] = useState(false);
  const [cnpj, setCnpj] = useState('');
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [salvandoEmpresa, setSalvandoEmpresa] = useState(false);

  const carregarContratos = async () => {
    setLoading(true);
    try {
      const r = await api.get('/frota-admin/contratos');
      setContratos(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(
        (e.response && e.response.data && e.response.data.erro) ||
          'Falha ao carregar contratos'
      );
    }
    setLoading(false);
  };

  const carregarEmpresas = async () => {
    try {
      const r = await api.get('/frota-admin/empresas');
      setEmpresas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Falha ao carregar empresas');
    }
  };

  useEffect(() => {
    carregarContratos();
    carregarEmpresas();
  }, []);

  const abrirNovo = () => {
    setEditandoId(null);
    setForm(FORM_VAZIO);
    setModalContrato(true);
  };

  const abrirEdicao = c => {
    setEditandoId(c.id);
    setForm({
      numero_contrato: c.numero_contrato || '',
      sei: c.sei || '',
      vigencia_inicio: soData(c.vigencia_inicio),
      vigencia_fim: soData(c.vigencia_fim),
      valor: c.valor === null || c.valor === undefined ? '' : String(c.valor),
      aditivo: c.aditivo === true,
      fk_empresas_id: c.fk_empresas_id ? String(c.fk_empresas_id) : '',
    });
    setModalContrato(true);
  };

  const campo = (nome, valor) => setForm(f => ({ ...f, [nome]: valor }));

  const salvarContrato = async () => {
    setSalvando(true);
    try {
      const corpo = { ...form, valor: form.valor === '' ? '' : Number(form.valor) };
      if (editandoId) {
        await api.put(`/frota-admin/contratos/${editandoId}`, corpo);
        toast.success('Contrato atualizado');
      } else {
        await api.post('/frota-admin/contratos', corpo);
        toast.success('Contrato cadastrado');
      }
      setModalContrato(false);
      await carregarContratos();
    } catch (e) {
      // Modal segue aberto com os dados preenchidos — o usuario corrige e tenta de novo.
      toast.error(
        (e.response && e.response.data && e.response.data.erro) ||
          'Falha ao salvar o contrato'
      );
    }
    setSalvando(false);
  };

  const alternarAtivo = async c => {
    try {
      await api.patch(`/frota-admin/contratos/${c.id}/ativo`, { ativo: !c.ativo });
      toast.success(c.ativo ? 'Contrato inativado' : 'Contrato ativado');
      await carregarContratos();
    } catch (e) {
      toast.error(
        (e.response && e.response.data && e.response.data.erro) ||
          'Falha ao alterar a situação'
      );
    }
  };

  const salvarEmpresa = async () => {
    setSalvandoEmpresa(true);
    try {
      const r = await api.post('/frota-admin/empresas', { cnpj, nome: nomeEmpresa });
      toast.success('Empresa cadastrada');
      setModalEmpresa(false);
      setCnpj('');
      setNomeEmpresa('');
      await carregarEmpresas();
      // Ja deixa selecionada no contrato em edicao — sem perder o que foi digitado.
      campo('fk_empresas_id', String(r.data.id));
    } catch (e) {
      toast.error(
        (e.response && e.response.data && e.response.data.erro) ||
          'Falha ao cadastrar a empresa'
      );
    }
    setSalvandoEmpresa(false);
  };

  return (
    <NavBar>
      <MDBContainer>
        <MDBRow className="align-items-center mt-3">
          <MDBCol col="12" md="8">
            <h3 className="h3-responsive font-weight-bolder">Contratos de Frota</h3>
          </MDBCol>
          <MDBCol col="12" md="4" className="text-right">
            <MDBBtn color="primary" size="sm" onClick={abrirNovo}>
              <MDBIcon icon="plus" className="mr-1" /> Novo contrato
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        <MDBCard className="mt-3">
          <MDBCardBody>
            {loading ? (
              <Loading />
            ) : (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Contrato</th>
                      <th>Empresa</th>
                      <th>Vigência</th>
                      <th>Situação</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {contratos.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center grey-text">
                          Nenhum contrato cadastrado nesta gerência.
                        </td>
                      </tr>
                    )}
                    {contratos.map(c => (
                      <tr key={c.id}>
                        <td>{c.numero_contrato}</td>
                        <td>{c.empresa ? c.empresa.nome : '-'}</td>
                        <td>
                          {paraTela(c.vigencia_inicio)} a {paraTela(c.vigencia_fim)}
                        </td>
                        <td>{c.ativo ? 'Ativo' : 'Inativo'}</td>
                        <td className="text-right">
                          <MDBBtn
                            size="sm"
                            color="light"
                            onClick={() => abrirEdicao(c)}
                          >
                            Editar
                          </MDBBtn>
                          <MDBBtn
                            size="sm"
                            color={c.ativo ? 'warning' : 'success'}
                            onClick={() => alternarAtivo(c)}
                          >
                            {c.ativo ? 'Inativar' : 'Ativar'}
                          </MDBBtn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </MDBCardBody>
        </MDBCard>

        {/* modal criar/editar contrato */}
        <MDBModal isOpen={modalContrato} toggle={() => setModalContrato(false)} size="lg">
          <MDBModalHeader toggle={() => setModalContrato(false)}>
            {editandoId ? 'Editar contrato' : 'Novo contrato'}
          </MDBModalHeader>
          <MDBModalBody>
            <MDBRow>
              <MDBCol col="12" md="9">
                <label className="grey-text">
                  <Obrigatorio texto="Empresa contratada" />
                </label>
                <select
                  className="browser-default custom-select"
                  value={form.fk_empresas_id}
                  onChange={e => campo('fk_empresas_id', e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {empresas.map(e => (
                    <option key={e.id} value={String(e.id)}>
                      {e.nome}
                    </option>
                  ))}
                </select>
              </MDBCol>
              <MDBCol col="12" md="3" className="d-flex align-items-end">
                <MDBBtn size="sm" color="light" onClick={() => setModalEmpresa(true)}>
                  <MDBIcon icon="plus" className="mr-1" /> Nova empresa
                </MDBBtn>
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol col="12" md="6">
                <MDBInput
                  label={<Obrigatorio texto="Número do contrato" />}
                  outline
                  maxLength={20}
                  value={form.numero_contrato}
                  onChange={e => campo('numero_contrato', e.target.value)}
                />
              </MDBCol>
              <MDBCol col="12" md="6">
                <MDBInput
                  label={<Obrigatorio texto="SEI" />}
                  outline
                  maxLength={40}
                  value={form.sei}
                  onChange={e => campo('sei', e.target.value)}
                />
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol col="12" md="6">
                <MDBInput
                  label={<Obrigatorio texto="Início da vigência" />}
                  outline
                  type="date"
                  value={form.vigencia_inicio}
                  onChange={e => campo('vigencia_inicio', e.target.value)}
                />
              </MDBCol>
              <MDBCol col="12" md="6">
                <MDBInput
                  label={<Obrigatorio texto="Fim da vigência" />}
                  outline
                  type="date"
                  value={form.vigencia_fim}
                  onChange={e => campo('vigencia_fim', e.target.value)}
                />
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol col="12" md="6">
                <MDBInput
                  label={<Obrigatorio texto="Valor" />}
                  outline
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.valor}
                  onChange={e => campo('valor', e.target.value)}
                />
              </MDBCol>
              <MDBCol col="12" md="6" className="d-flex align-items-center">
                <MDBInput
                  label="Aditivo"
                  type="checkbox"
                  id="contrato-aditivo"
                  checked={form.aditivo}
                  onChange={e => campo('aditivo', e.target.checked)}
                />
              </MDBCol>
            </MDBRow>
            <LegendaObrigatorios />
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="light" size="sm" onClick={() => setModalContrato(false)}>
              Cancelar
            </MDBBtn>
            <MDBBtn color="primary" size="sm" disabled={salvando} onClick={salvarContrato}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* modal nova empresa */}
        <MDBModal isOpen={modalEmpresa} toggle={() => setModalEmpresa(false)}>
          <MDBModalHeader toggle={() => setModalEmpresa(false)}>Nova empresa</MDBModalHeader>
          <MDBModalBody>
            <MDBInput
              label={<Obrigatorio texto="CNPJ" />}
              outline
              maxLength={18}
              value={cnpj}
              onChange={e => setCnpj(e.target.value)}
            />
            <MDBInput
              label={<Obrigatorio texto="Nome da empresa" />}
              outline
              maxLength={200}
              value={nomeEmpresa}
              onChange={e => setNomeEmpresa(e.target.value)}
            />
            <LegendaObrigatorios />
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="light" size="sm" onClick={() => setModalEmpresa(false)}>
              Cancelar
            </MDBBtn>
            <MDBBtn
              color="primary"
              size="sm"
              disabled={salvandoEmpresa}
              onClick={salvarEmpresa}
            >
              {salvandoEmpresa ? 'Salvando...' : 'Salvar'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>
      </MDBContainer>
    </NavBar>
  );
}
